import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CalendarDays, Mail, Lightbulb } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { MemberQuickNav } from '../MemberQuickNav';
import { useContent } from '../../context/ContentContext';
import api from '../../lib/api';

const BOOK_IMG = 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/15d41a15013701bc4ecb47f44b6dc86f6fec9d2bd5bef7a60e5bf90bb6f92e2c.jpeg';

const SuggestionBox = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ title: '', author: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/book-suggestions').then((res) => setSuggestions(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/book-suggestions', form);
      toast.success('Thanks — your suggestion is in the running!');
      setForm({ title: '', author: '', note: '' });
      load();
    } catch {
      toast.error('Could not save your suggestion — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-14 grid md:grid-cols-2 gap-10 items-start">
      <div className="bg-white rounded-xl shadow-md border border-[#bd8cae]/20 p-6" data-testid="book-suggestion-box">
        <p className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#a3688f] font-medium mb-3">
          <Lightbulb className="w-4 h-4" /> Suggest a Book
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Input
            required
            placeholder="Book title *"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="suggestion-title-input"
          />
          <Input
            placeholder="Author (optional)"
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            data-testid="suggestion-author-input"
          />
          <Textarea
            rows={3}
            placeholder="Why this one? (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="suggestion-note-input"
          />
          <Button type="submit" disabled={saving} data-testid="suggestion-submit-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white w-full">
            {saving ? 'Submitting…' : 'Submit Suggestion'}
          </Button>
        </form>
      </div>

      <div data-testid="book-suggestions-list">
        <p className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#a3688f] font-medium mb-3">
          <BookOpen className="w-4 h-4" /> Member Suggestions
        </p>
        {suggestions.length === 0 ? (
          <p className="text-sm text-[#3c4a2a]/50 bg-white/60 rounded-lg p-4">No suggestions yet — be the first to nominate a book!</p>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s) => (
              <div key={s.id} className="bg-white rounded-lg shadow-sm border border-[#bd8cae]/20 p-4" data-testid={`suggestion-item-${s.id}`}>
                <p className="font-medium text-[#3c4a2a]">
                  {s.title}{s.author ? <span className="text-[#3c4a2a]/60 font-normal"> — {s.author}</span> : null}
                </p>
                {s.note && <p className="text-sm text-[#3c4a2a]/70 mt-1">{s.note}</p>}
                <p className="text-xs text-[#a3688f] mt-2">Suggested by {s.suggested_by}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function BookClubPage() {
  const { c } = useContent();

  return (
    <main className="min-h-screen bg-[#f1e3eb] pt-44 pb-20 px-4" data-testid="book-club-page">
      <div className="max-w-4xl mx-auto">
        <MemberQuickNav />
        <div className="text-center mb-12">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Members Only</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-2">
            {c('bookclub.heading', 'Book Club Corner')}
          </h1>
          <p className="text-[#3c4a2a]/70 mt-3 max-w-xl mx-auto">
            {c('bookclub.intro', 'Good books, great company. Join fellow members for lively discussion — no literary degree required, just bring your thoughts (and maybe a snack).')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
              <img
                src={c('bookclub.current.image', BOOK_IMG)}
                alt="Current book club pick"
                className="w-full h-full object-cover"
                data-testid="book-club-image"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 w-24 h-24 bg-[#bd8cae]/30 rounded-full -z-10" />
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md border border-[#bd8cae]/20 p-6" data-testid="book-club-current-pick">
              <p className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#a3688f] font-medium mb-3">
                <BookOpen className="w-4 h-4" /> Current Pick
              </p>
              <h2 className="font-serif text-2xl text-[#3c4a2a]">
                {c('bookclub.current.title', 'Our First Pick — To Be Announced!')}
              </h2>
              {c('bookclub.current.author', '') && (
                <p className="text-[#3c4a2a]/60 mt-1">by {c('bookclub.current.author', '')}</p>
              )}
              <p className="text-[#3c4a2a]/75 leading-relaxed mt-3">
                {c('bookclub.current.blurb', "We'll choose our first book together at an upcoming meeting — bring your suggestions! Fiction, memoir, or something inspiring: every voice helps pick.")}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-[#bd8cae]/20 p-6" data-testid="book-club-meeting-info">
              <p className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#a3688f] font-medium mb-3">
                <CalendarDays className="w-4 h-4" /> Meeting Info
              </p>
              <p className="text-[#3c4a2a]/80 leading-relaxed whitespace-pre-wrap">
                {c('bookclub.meeting.info', 'Book club gathering details will be announced soon — keep an eye on your email and the Club Calendar.')}
              </p>
            </div>

            <div className="bg-[#5d6b3c] rounded-xl p-6 text-white">
              <p className="flex items-center gap-2 text-sm uppercase tracking-wider text-white/70 font-medium mb-2">
                <Mail className="w-4 h-4" /> Want In?
              </p>
              <p className="text-white/90 text-sm leading-relaxed mb-4">
                {c('bookclub.join.text', 'Mark "Yes" to the book club question on your Member Information Form and we\'ll keep you in the loop.')}
              </p>
              <Link to="/member-info">
                <Button data-testid="book-club-join-btn" className="bg-white text-[#5d6b3c] hover:bg-[#f1e3eb]">
                  Update My Info Form
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <SuggestionBox />
      </div>
    </main>
  );
}
